import React, { useState } from "react";
import PropTypes from "prop-types";

import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import Collapse from "@mui/material/Collapse";
import Grid from "@mui/material/Grid";
import LinearProgress from "@mui/material/LinearProgress";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { alpha, useTheme } from "@mui/material/styles";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

import { getMatchPageSectionSx } from "./matchPageSectionStyles";

function formatPercent(value) {
  if (value == null) {
    return "N/A";
  }
  return `${Math.round((Number(value) || 0) * 100)}%`;
}

function formatMoney(value) {
  return `${Math.round(Number(value) || 0)}`;
}

function formatMs(value) {
  if (value == null) return "N/A";
  return `${Math.round(Number(value) || 0)}ms`;
}

function formatDeg(value) {
  if (value == null) return "N/A";
  return `${(Math.round((Number(value) || 0) * 10) / 10).toFixed(1)}°`;
}

function formatRating(value) {
  if (value == null) return "N/A";
  return (Number(value) || 0).toFixed(2);
}

function formatKd(value) {
  if (value == null) return "N/A";
  return (Number(value) || 0).toFixed(2);
}

function formatAdr(value) {
  if (value == null) return "N/A";
  return `${(Math.round((Number(value) || 0) * 10) / 10).toFixed(1)}`;
}

function formatMetricRangeValue(value, unit) {
  if (value == null) return "N/A";
  if (unit === "ms") return `${Math.round(Number(value) || 0)}ms`;
  if (unit === "deg") return `${(Math.round((Number(value) || 0) * 10) / 10).toFixed(1)}°`;
  return `${Math.round((Number(value) || 0) * 10) / 10}`;
}

function getMetricRange(m, key) {
  return m.engagementMetrics?.[key] || null;
}

function getAnticheatMetricConfig(player) {
  return [
    {
      key: "killContextCount",
      label: "Kill Contexts",
      value: `${player.metrics.killContextCount}`,
      barValue: Math.min(100, player.metrics.killContextCount * 12),
    },
    {
      key: "zeroUtilityFlags",
      label: "Low-Utility Flags",
      value: `${player.metrics.zeroUtilityFlags}`,
      barValue: Math.min(100, player.metrics.zeroUtilityFlags * 25),
    },
    {
      key: "headshotRate",
      label: "Headshot Rate",
      value: formatPercent(player.metrics.headshotRate),
      barValue: (player.metrics.headshotRate || 0) * 100,
    },
    {
      key: "smokeKillRate",
      label: "Smoke Kill %",
      value: formatPercent(player.metrics.smokeKillRate),
      barValue: (player.metrics.smokeKillRate || 0) * 100,
    },
    {
      key: "wallbangKillRate",
      label: "Wallbang Kill %",
      value: formatPercent(player.metrics.wallbangKillRate),
      barValue: (player.metrics.wallbangKillRate || 0) * 100,
    },
    {
      key: "blindedCount",
      label: "Blinded Events",
      value: `${player.metrics.blindedCount}`,
      barValue: Math.min(100, player.metrics.blindedCount * 20),
    },
  ];
}

function getStatsMetricConfig(player) {
  const m = player.metrics;
  const timeToDamage = getMetricRange(m, "timeToDamageMs");
  const visibilityToDamage = getMetricRange(m, "visibilityToDamageMs");
  const reactionTime = getMetricRange(m, "reactionTimeMs");
  const crosshairPlacement = getMetricRange(m, "crosshairPlacementDeg");
  const preaim = getMetricRange(m, "preaimDeg");

  return [
    {
      key: "timeToDamageMs",
      label: timeToDamage?.label || "Shot to Damage",
      value: timeToDamage
        ? formatMetricRangeValue(timeToDamage.avg, timeToDamage.unit)
        : formatMs(m.timeToDamageMs),
      barValue:
        m.timeToDamageMs != null
          ? Math.max(0, 100 - (m.timeToDamageMs / (timeToDamage?.maxForBar || 1000)) * 100)
          : 0,
      range: timeToDamage,
      description:
        timeToDamage?.description ||
        "Current match timing sample from first shot to first registered bullet damage.",
    },
    {
      key: "visibilityToDamageMs",
      label: visibilityToDamage?.label || "Visibility to Damage",
      value: visibilityToDamage
        ? formatMetricRangeValue(visibilityToDamage.avg, visibilityToDamage.unit)
        : formatMs(m.visibilityToDamageMs),
      barValue:
        m.visibilityToDamageMs != null
          ? Math.max(0, 100 - (m.visibilityToDamageMs / (visibilityToDamage?.maxForBar || 2000)) * 100)
          : 0,
      range: visibilityToDamage,
      description:
        visibilityToDamage?.description ||
        "Current match average from first spotting an enemy to first registered bullet damage.",
    },
    {
      key: "reactionTimeMs",
      label: reactionTime?.label || "Return Fire Time",
      value: reactionTime
        ? formatMetricRangeValue(reactionTime.avg, reactionTime.unit)
        : formatMs(m.reactionTimeMs),
      barValue:
        m.reactionTimeMs != null
          ? Math.max(0, 100 - (m.reactionTimeMs / (reactionTime?.maxForBar || 1000)) * 100)
          : 0,
      range: reactionTime,
      description:
        reactionTime?.description ||
        "Current match timing sample from taking damage to the next shot.",
    },
    {
      key: "crosshairPlacementDeg",
      label: crosshairPlacement?.label || "Damage Crosshair Angle",
      value: crosshairPlacement
        ? formatMetricRangeValue(crosshairPlacement.avg, crosshairPlacement.unit)
        : formatDeg(m.crosshairPlacementDeg),
      barValue:
        m.crosshairPlacementDeg != null
          ? Math.max(0, 100 - (m.crosshairPlacementDeg / 45) * 100)
          : 0,
      range: crosshairPlacement,
      description:
        crosshairPlacement?.description ||
        "Current match angle to the victim's head at first bullet damage.",
    },
    {
      key: "preaimDeg",
      label: preaim?.label || "Pre-Fire Aim Angle",
      value: preaim
        ? formatMetricRangeValue(preaim.avg, preaim.unit)
        : formatDeg(m.preaimDeg),
      barValue:
        m.preaimDeg != null ? Math.max(0, 100 - (m.preaimDeg / 45) * 100) : 0,
      range: preaim,
      description:
        preaim?.description ||
        "Current match angle to the nearest alive enemy head at first shot.",
    },
    {
      key: "kdRatio",
      label: "K/D Ratio",
      value: formatKd(m.kdRatio),
      barValue: Math.min(100, ((m.kdRatio || 0) / 2) * 100),
      description: "Current match kills divided by deaths. Uses scoreboard data when available.",
    },
    {
      key: "adr",
      label: "ADR",
      value: formatAdr(m.adr),
      barValue: Math.min(100, m.adr || 0),
      description: "Current match average damage per round. Uses scoreboard data when available.",
    },
    {
      key: "aimAccuracy",
      label: "Aim Accuracy",
      value: formatPercent(m.aimAccuracy),
      barValue: (m.aimAccuracy || 0) * 100,
      description: "Current match bullet hits divided by weapon shots recorded by the plugin.",
    },
    {
      key: "headAccuracy",
      label: "Head Accuracy",
      value: formatPercent(m.headAccuracy),
      barValue: (m.headAccuracy || 0) * 100,
      description: "Current match head hitgroup hits divided by landed bullet hits.",
    },
    {
      key: "wallbangKillRate",
      label: "Wallbang Kill %",
      value: formatPercent(m.wallbangKillRate),
      barValue: (m.wallbangKillRate || 0) * 100,
      description: "Current match kills through penetration divided by total kills.",
    },
    {
      key: "smokeKillRate",
      label: "Smoke Kill %",
      value: formatPercent(m.smokeKillRate),
      barValue: (m.smokeKillRate || 0) * 100,
      description: "Current match kills through smoke divided by total kills.",
    },
    {
      key: "hltvRating2",
      label: "HLTV Rating 2.0",
      value: formatRating(m.hltvRating2),
      barValue: Math.min(100, ((m.hltvRating2 || 0) / 2) * 100),
      description: "Current match HLTV 2.0-style approximation from KPR, DPR, ADR, impact and KAST.",
    },
    {
      key: "kastRate",
      label: "KAST",
      value: formatPercent(m.kastRate),
      barValue: (m.kastRate || 0) * 100,
      description: "Current match KST approximation: rounds with a kill, survival, or trade.",
    },
  ];
}

function getEconomyMetricConfig(player) {
  return [
    {
      key: "utilityBuyRate",
      label: "Utility Buy Rate",
      value: formatPercent(player.metrics.utilityBuyRate),
      barValue: (player.metrics.utilityBuyRate || 0) * 100,
    },
    {
      key: "trackedUtilityUseRate",
      label: "Tracked Utility Use",
      value: formatPercent(player.metrics.trackedUtilityUseRate),
      barValue: (player.metrics.trackedUtilityUseRate || 0) * 100,
    },
    {
      key: "ecoPressureRate",
      label: "Low-Money Starts",
      value: formatPercent(player.metrics.ecoPressureRate),
      barValue: (player.metrics.ecoPressureRate || 0) * 100,
    },
    {
      key: "cashFloatRate",
      label: "Cash Float Rate",
      value: formatPercent(player.metrics.cashFloatRate),
      barValue: (player.metrics.cashFloatRate || 0) * 100,
    },
    {
      key: "averageSpend",
      label: "Average Spend",
      value: formatMoney(player.metrics.averageSpend),
      barValue: Math.min(100, ((player.metrics.averageSpend || 0) / 5000) * 100),
    },
    {
      key: "roundCount",
      label: "Tracked Rounds",
      value: `${player.metrics.roundCount}`,
      barValue: Math.min(100, player.metrics.roundCount * 8),
    },
  ];
}

function getUtilityMetricConfig(player) {
  const m = player.metrics;
  const hasBehavior = m.hasBehaviorData;
  return [
    {
      key: "usedPurchaseRate",
      label: "Purchases Used",
      value: hasBehavior && m.usedPurchaseRate != null ? formatPercent(m.usedPurchaseRate) : "N/A",
      barValue: hasBehavior ? (m.usedPurchaseRate || 0) * 100 : 0,
      theme: "positive",
    },
    {
      key: "savedPurchaseRate",
      label: "Purchases Saved",
      value: hasBehavior && m.savedPurchaseRate != null ? formatPercent(m.savedPurchaseRate) : "N/A",
      barValue: hasBehavior ? (m.savedPurchaseRate || 0) * 100 : 0,
      theme: "neutral",
    },
    {
      key: "wastedPurchaseRate",
      label: "Purchases Wasted",
      value: hasBehavior && m.wastedPurchaseRate != null ? formatPercent(m.wastedPurchaseRate) : "N/A",
      barValue: hasBehavior ? (m.wastedPurchaseRate || 0) * 100 : 0,
      theme: "negative",
    },
    {
      key: "enemyUtilityDamage",
      label: "Enemy Util Damage",
      value: hasBehavior && m.enemyUtilityDamage != null ? `${Math.round(m.enemyUtilityDamage)}` : "N/A",
      barValue: hasBehavior ? Math.min(100, ((m.enemyUtilityDamage || 0) / 300) * 100) : 0,
      theme: "positive",
    },
    {
      key: "friendlyUtilityDamage",
      label: "Friendly Util Damage",
      value: hasBehavior && m.friendlyUtilityDamage != null ? `${Math.round(m.friendlyUtilityDamage)}` : "N/A",
      barValue: hasBehavior ? Math.min(100, ((m.friendlyUtilityDamage || 0) / 100) * 100) : 0,
      theme: "negative",
    },
    {
      key: "harmfulFlashRate",
      label: "Harmful Flash Rate",
      value: hasBehavior && m.harmfulFlashRate != null ? formatPercent(m.harmfulFlashRate) : "N/A",
      barValue: hasBehavior ? (m.harmfulFlashRate || 0) * 100 : 0,
      theme: "negative",
    },
  ];
}

function getUtilityBadgeConfig(player) {
  const m = player.metrics;
  if (!m.hasBehaviorData) {
    return { label: "No Data", color: "#9CA3AF", background: "rgba(156,163,175,0.1)" };
  }
  const isHarmful = m.harmfulFlashRate != null && m.harmfulFlashRate >= 0.35;
  const isWasteful = m.wastedPurchaseRate != null && m.wastedPurchaseRate >= 0.3;
  const isEfficient = m.usedPurchaseRate != null && m.usedPurchaseRate >= 0.7 && !isHarmful;

  if (isHarmful) {
    return { label: "Harmful", color: "#ff6b7d", background: "rgba(255,107,125,0.12)" };
  }
  if (isWasteful) {
    return { label: "Wasteful", color: "#FF9800", background: "rgba(255,152,0,0.12)" };
  }
  if (isEfficient) {
    return { label: "Efficient", color: "#5ad17b", background: "rgba(90,209,123,0.12)" };
  }
  return { label: "Balanced", color: "#A78BFA", background: "rgba(232,163,61,0.12)" };
}

function getBadgeConfig(sectionType, player) {
  if (sectionType === "stats") {
    return null;
  }

  if (sectionType === "anticheat") {
    const configs = {
      clean: {
        label: "Clean",
        color: "#5ad17b",
        background: alpha("#5ad17b", 0.12),
      },
      watch: {
        label: "Watch",
        color: "#FF9800",
        background: alpha("#FF9800", 0.12),
      },
      notable: {
        label: "Notable",
        color: "#ff6b7d",
        background: alpha("#ff6b7d", 0.12),
      },
    };
    return configs[player.verdict] || configs.clean;
  }

  if (sectionType === "utility") {
    return getUtilityBadgeConfig(player);
  }

  return {
    label: player.metrics.buyStyle || "balanced",
    color: "#A78BFA",
    background: alpha("#E8A33D", 0.12),
  };
}

function getMetricConfig(sectionType, player) {
  if (sectionType === "anticheat") return getAnticheatMetricConfig(player);
  if (sectionType === "stats") return getStatsMetricConfig(player);
  if (sectionType === "utility") return getUtilityMetricConfig(player);
  return getEconomyMetricConfig(player);
}

const UTILITY_NEGATIVE_KEYS = new Set(["wastedPurchaseRate", "friendlyUtilityDamage", "harmfulFlashRate"]);
const UTILITY_POSITIVE_KEYS = new Set(["usedPurchaseRate", "enemyUtilityDamage"]);

function getMetricBarColor({ sectionType, player, metricKey }) {
  if (sectionType === "anticheat") {
    if (player.highlightedMetricKeys?.includes(metricKey)) {
      return "#FF9800";
    }
    return "#57d476";
  }

  if (sectionType === "stats") {
    return "#57d476";
  }

  if (sectionType === "utility") {
    if (UTILITY_NEGATIVE_KEYS.has(metricKey)) return "#ff6b7d";
    if (UTILITY_POSITIVE_KEYS.has(metricKey)) return "#5ad17b";
    return "#4cc9f0";
  }

  return "#4cc9f0";
}

function getRangePercent(value, range) {
  const maxForBar = Number(range?.maxForBar) || 100;
  return Math.max(0, Math.min(100, ((Number(value) || 0) / maxForBar) * 100));
}

function MetricRangeSummary({ range }) {
  if (!range) {
    return null;
  }

  const minPct = getRangePercent(range.min, range);
  const avgPct = getRangePercent(range.avg, range);
  const maxPct = getRangePercent(range.max, range);
  const leftPct = Math.min(minPct, maxPct);
  const widthPct = Math.max(2, Math.abs(maxPct - minPct));
  const isLowSample = range.confidence === "low_sample";
  const rangeTextSx = {
    color: "rgba(255,255,255,0.6)",
    fontSize: "0.72rem",
    fontWeight: 500,
    lineHeight: 1.35,
  };

  return (
    <Box sx={{ mt: 1 }}>
      <Box
        sx={{
          position: "relative",
          height: 8,
          borderRadius: 999,
          backgroundColor: "rgba(255,255,255,0.08)",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            position: "absolute",
            left: `${leftPct}%`,
            width: `${widthPct}%`,
            top: 0,
            bottom: 0,
            borderRadius: 999,
            backgroundColor: alpha("#57d476", 0.28),
          }}
        />
        <Box
          sx={{
            position: "absolute",
            left: `${avgPct}%`,
            top: -2,
            bottom: -2,
            width: 2,
            borderRadius: 999,
            backgroundColor: isLowSample ? "#FF9800" : "#57d476",
            boxShadow: `0 0 0 2px ${alpha("#0f0f10", 0.9)}`,
          }}
        />
      </Box>
      <Stack
        direction="row"
        spacing={1}
        flexWrap="wrap"
        useFlexGap
        sx={{ mt: 0.75 }}
      >
        <Typography component="span" sx={rangeTextSx}>
          Min {formatMetricRangeValue(range.min, range.unit)}
        </Typography>
        <Typography component="span" sx={rangeTextSx}>
          Avg {formatMetricRangeValue(range.avg, range.unit)}
        </Typography>
        <Typography component="span" sx={rangeTextSx}>
          Max {formatMetricRangeValue(range.max, range.unit)}
        </Typography>
        <Typography component="span" sx={rangeTextSx}>
          n={range.count}
        </Typography>
        {isLowSample ? (
          <Typography component="span" sx={{ ...rangeTextSx, color: "#FF9800" }}>
            Low sample
          </Typography>
        ) : null}
      </Stack>
    </Box>
  );
}

export default function MatchAnalysisSection({
  sectionType,
  eyebrow,
  title,
  summary,
  players,
  emptyTitle,
  emptyText,
}) {
  const theme = useTheme();
  const sx = getMatchPageSectionSx(theme);
  const [isExpanded, setIsExpanded] = useState(false);
  const contentId = `match-analysis-${sectionType}-content`;

  return (
    <Box sx={sx.sectionPanel}>
      <Box
        component="button"
        type="button"
        aria-expanded={isExpanded}
        aria-controls={contentId}
        onClick={() => setIsExpanded((current) => !current)}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
          width: "100%",
          p: 0,
          m: 0,
          border: 0,
          background: "transparent",
          color: "inherit",
          cursor: "pointer",
          textAlign: "left",
        }}
      >
        <Box>
          <Typography sx={{ ...sx.sectionEyebrow, mb: 0.75 }}>{eyebrow}</Typography>
          <Typography sx={{ ...sx.sectionTitle, mb: 0 }}>{title}</Typography>
        </Box>
        <Box
          aria-hidden="true"
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: 36,
            height: 36,
            flex: "0 0 auto",
            borderRadius: "999px",
            color: "#ffffff",
            backgroundColor: "rgba(255,255,255,0.05)",
            border: "1px solid rgba(255,255,255,0.08)",
          }}
        >
          <ExpandMoreIcon
            sx={{
              fontSize: 24,
              transform: isExpanded ? "rotate(180deg)" : "rotate(0deg)",
              transition: "transform 0.15s ease-in-out",
            }}
          />
        </Box>
      </Box>

      <Collapse in={isExpanded} timeout={180} unmountOnExit>
        <Box id={contentId} sx={{ pt: 2 }}>
          {summary ? (
            <Stack spacing={0.75} sx={{ mb: 2.5 }}>
              <Typography sx={{ color: "#ffffff", fontSize: "1rem", fontWeight: 700 }}>
                {summary.headline}
              </Typography>
              <Typography sx={sx.sectionBody}>{summary.detail}</Typography>
            </Stack>
          ) : null}

          {!Array.isArray(players) || players.length === 0 ? (
            <Box sx={sx.emptyState}>
              <Typography sx={{ fontWeight: 700, color: "#ffffff", mb: 0.6 }}>
                {emptyTitle}
              </Typography>
              <Typography sx={sx.sectionBody}>{emptyText}</Typography>
            </Box>
          ) : (
            <Grid container spacing={2}>
              {players.map((player) => {
                const badge = getBadgeConfig(sectionType, player);
                const metrics = getMetricConfig(sectionType, player);
                const playerSummary = sectionType === "stats" ? null : player.summary;

                return (
                  <Grid item xs={12} md={6} key={`${sectionType}-${player.steamId}`}>
                    <Box sx={sx.teamCard}>
                      <Stack
                        direction={{ xs: "column", sm: "row" }}
                        justifyContent="space-between"
                        alignItems={{ xs: "flex-start", sm: "center" }}
                        spacing={1.25}
                        sx={{ mb: 1.5 }}
                      >
                        <Stack spacing={0.35}>
                          <Typography sx={{ color: "#ffffff", fontSize: "1.05rem", fontWeight: 700 }}>
                            {player.name}
                          </Typography>
                          {playerSummary ? (
                            <Typography sx={sx.sectionBody}>{playerSummary}</Typography>
                          ) : null}
                        </Stack>
                        {badge ? (
                          <Chip
                            label={badge.label}
                            sx={{
                              ...sx.neutralBadge,
                              color: badge.color,
                              backgroundColor: badge.background,
                              textTransform: "uppercase",
                            }}
                          />
                        ) : null}
                      </Stack>

                      <Grid container spacing={1.5}>
                        {metrics.map((metric) => (
                          <Grid item xs={12} sm={6} key={`${player.steamId}-${metric.key}`}>
                            <Box sx={sx.metricCard}>
                              <Stack
                                direction="row"
                                justifyContent="space-between"
                                alignItems="center"
                                spacing={1}
                              >
                                <Typography sx={sx.metricLabel}>{metric.label}</Typography>
                                <Typography sx={sx.metricValue}>{metric.value}</Typography>
                              </Stack>
                              {metric.range ? (
                                <MetricRangeSummary range={metric.range} />
                              ) : (
                                <LinearProgress
                                  variant="determinate"
                                  value={Math.max(0, Math.min(100, metric.barValue || 0))}
                                  sx={{
                                    ...sx.progressTrack,
                                    "& .MuiLinearProgress-bar": {
                                      backgroundColor: getMetricBarColor({
                                        sectionType,
                                        player,
                                        metricKey: metric.key,
                                      }),
                                    },
                                  }}
                                />
                              )}
                              {metric.description ? (
                                <Typography
                                  sx={{
                                    mt: 0.75,
                                    color: "rgba(255,255,255,0.52)",
                                    fontSize: "0.72rem",
                                    lineHeight: 1.45,
                                  }}
                                >
                                  {metric.description}
                                </Typography>
                              ) : null}
                            </Box>
                          </Grid>
                        ))}
                      </Grid>
                    </Box>
                  </Grid>
                );
              })}
            </Grid>
          )}
        </Box>
      </Collapse>
    </Box>
  );
}

MatchAnalysisSection.propTypes = {
  sectionType: PropTypes.oneOf(["anticheat", "stats", "economy", "utility"]).isRequired,
  eyebrow: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  summary: PropTypes.shape({
    headline: PropTypes.string,
    detail: PropTypes.string,
  }),
  players: PropTypes.array,
  emptyTitle: PropTypes.string.isRequired,
  emptyText: PropTypes.string.isRequired,
};
